import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useCallback, useRef } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useUnmount } from 'react-use';
import cn from 'classnames';

import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';

import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import styles from './ChangeKnoInMeasureForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { startProcessFormSubmit } from '../../../ChangeKnoInMeasurePackage/actions/ChangeKnoInMeasureActions';
import { ErvkKnoOrganisationSearch } from '../../../ChangeKnoInMeasurePackage/components/ErvkKnoOrganisationSearch';
import { MeasureNumberSearch } from '../../../ChangeKnoInMeasurePackage/components/MeasureNumberSearch';
import { MeasureKnoChangeHistory } from '../../../ChangeKnoInMeasurePackage/components/MeasureKnoChangeHistory';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { IChangeKnoInMeasureForm } from '../interfaces/IChangeKnoInMeasureForm';
import { composeValidators } from '../../../CorePackage/MainModule/validators/composeValidators';

type ChangeKnoInMeasureFormRenderProps = FormRenderProps<IChangeKnoInMeasureForm>;

const ChangeKnoInMeasureForm = () => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const formRef = useRef<ChangeKnoInMeasureFormRenderProps['form']>();

  const handleFormSubmit = useCallback(
    (values: IChangeKnoInMeasureForm) => {
      const changeReason = values?.changeReason?.trim();

      if (values?.measureErpId && changeReason && values?.ervkKnoOrganization && values?.measureSystem) {
        dispatch(
          startProcessFormSubmit(values.measureErpId, changeReason, values.ervkKnoOrganization, values.measureSystem),
        );
      }
    },
    [dispatch],
  );

  useUnmount(() => {
    formRef.current?.restart();
  });

  function renderForm({ handleSubmit, form, values, invalid }: FormRenderProps<IChangeKnoInMeasureForm>) {
    formRef.current = form;
    const { changeReason, measureErpId, ervkKnoOrganization } = values;

    return (
      <form onSubmit={handleSubmit}>
        <div>
          <Row>
            <FieldBlock>
              <div style={{ display: 'flex' }}>
                <span style={{ fontWeight: 'bold', fontSize: 'xx-large' }}>Изменение КНО в мероприятии</span>
                <Button
                  className={cn('shared-btn-add', styles.ButtonSave)}
                  type="submit"
                  color="primary"
                  size="medium"
                  text="Сохранить"
                  disabled={invalid || !changeReason || !ervkKnoOrganization || !measureErpId}
                />
              </div>
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label={
                <>
                  Основание изменения
                  {!changeReason && <span className={styles.RedMark}> *</span>}
                </>
              }
              placementLabel="left"
            >
              <TextInputField
                className="shared-row-field"
                name="changeReason"
                validate={composeValidators(makeRequired(REQUIRED_ERROR))}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label={
                <>
                  Номер мероприятия
                  {!measureErpId && <span className={styles.RedMark}> *</span>}
                </>
              }
              placementLabel="left"
            >
              <MeasureNumberSearch formRef={formRef} />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label={
                <>
                  Новое КНО
                  {!ervkKnoOrganization && <span className={styles.RedMark}> *</span>}
                </>
              }
              placementLabel="left"
            >
              <ErvkKnoOrganisationSearch formRef={formRef} />
            </FieldBlock>
          </Row>

          <Row>
            <MeasureKnoChangeHistory />
          </Row>
        </div>
      </form>
    );
  }

  return (
    <Form<IChangeKnoInMeasureForm>
      onSubmit={handleFormSubmit}
      render={renderForm}
      mutators={{ ...arrayMutators }}
      validateOnBlur
    />
  );
};

export { ChangeKnoInMeasureForm };
